/**
 * IntelligencePage — AI/ML Ops & model governance (FND-011) + revenue
 * projection (AI-004).
 *
 * Two views behind tabs:
 *   - Model registry (GET /ai/models): capability, version, status, drift, and
 *     the approval gate — the governance surface for the advisory models that
 *     power the rest of the platform.
 *   - Revenue projection (GET /ai/revenue-projection): a forecast-vs-actual mini
 *     chart (inline CSS bars, no chart lib) rendered through an AIInsightCard so
 *     the advisory/HITL posture is explicit.
 *
 * Data via useModelRegistry() / useRevenueProjection(); both degrade gracefully
 * to a seed on 404 so the governance view always renders.
 */
import { useState, type CSSProperties } from 'react';
import {
  useModelRegistry,
  useRevenueProjection,
  type ModelRegistryEntry,
} from '../hooks/useIntelligence';
import { colors, radius, space } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { AIInsightCard } from '../components/AIInsightCard';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { Toolbar } from '../components/Toolbar';
import { useShellSlot } from '../components/shellContext';

function statusTier(status: string): 'success' | 'info' | 'warning' | 'neutral' {
  const s = status.toLowerCase();
  if (s === 'champion' || s === 'production') return 'success';
  if (s === 'challenger' || s === 'staging') return 'info';
  if (s === 'shadow') return 'warning';
  return 'neutral';
}

function gateTier(gate: string): 'success' | 'warning' | 'danger' {
  const g = gate.toLowerCase();
  if (g === 'approved') return 'success';
  if (g === 'pending_review' || g === 'pending') return 'warning';
  return 'danger';
}

function driftTier(drift: string): 'success' | 'warning' | 'danger' {
  const d = drift.toLowerCase();
  if (d === 'none' || d === 'low') return 'success';
  if (d === 'moderate') return 'warning';
  return 'danger';
}

function fmtCurrency(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

type Tab = 'registry' | 'projection';

export function IntelligencePage() {
  useShellSlot({ breadcrumb: 'Intelligence' });
  const registry = useModelRegistry();
  const projection = useRevenueProjection();
  const [tab, setTab] = useState<Tab>('registry');

  const anySeeded = !registry.available || !projection.available;

  const columns: Column<ModelRegistryEntry>[] = [
    { key: 'capability', header: 'Capability', render: (m) => <span style={{ fontWeight: 600 }}>{m.capability}</span> },
    { key: 'version', header: 'Version', render: (m) => <span style={muted}>{m.version}</span> },
    { key: 'status', header: 'Status', render: (m) => <StatusBadge status={statusTier(m.status)} label={m.status} /> },
    {
      key: 'drift',
      header: 'Drift',
      render: (m) => <StatusBadge status={driftTier(m.drift)} label={m.drift} />,
    },
    { key: 'last_eval', header: 'Last eval', align: 'right', render: (m) => <span style={muted}>{m.last_eval}</span> },
    {
      key: 'gate',
      header: 'Approval gate',
      render: (m) => <StatusBadge status={gateTier(m.approval_gate)} label={m.approval_gate.replace(/_/g, ' ')} />,
    },
  ];

  const proj = projection.projection;
  const projMax = proj ? Math.max(...proj.points.map((p) => Math.max(p.forecast, p.actual ?? 0)), 1) : 1;

  return (
    <>
      <PageHeader
        title="Intelligence & Model Governance"
        subtitle="The advisory AI/ML layer behind Enable 2.0: the model registry (capability, version, drift, approval gate) and the revenue-projection forecast. All models are advisory — humans confirm (FND-010/011, AI-004)."
        actions={
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              registry.refresh();
              projection.refresh();
            }}
          >
            Refresh
          </button>
        }
      />

      {anySeeded && (
        <p style={seedNotice} role="status">
          Showing seeded governance data for surfaces not yet wired. Live model telemetry appears
          automatically when the AI endpoints are online.
        </p>
      )}

      <Toolbar style={{ marginBottom: 16 }}>
        <div style={tabs}>
          <TabBtn active={tab === 'registry'} onClick={() => setTab('registry')}>
            Model registry
          </TabBtn>
          <TabBtn active={tab === 'projection'} onClick={() => setTab('projection')}>
            Revenue projection
          </TabBtn>
        </div>
      </Toolbar>

      {tab === 'registry' ? (
        <Card title="AI/ML model registry" padded={false}>
          {registry.isLoading && !registry.models.length ? (
            <div style={{ padding: 24 }}>
              <LoadingIndicator text="Loading model registry…" />
            </div>
          ) : (
            <DataTable
              columns={columns}
              rows={registry.models}
              rowKey={(m) => m.id}
              emptyTitle="No models registered"
            />
          )}
        </Card>
      ) : !proj ? (
        <Card>
          <LoadingIndicator text="Loading projection…" />
        </Card>
      ) : (
        <>
          <div style={projHeadRow}>
            <div style={projHeadCard}>
              <div style={projHeadValue}>{fmtCurrency(proj.projected_total)}</div>
              <div style={muted}>Projected portfolio revenue capture</div>
            </div>
            <div style={projHeadCard}>
              <div style={projHeadValue}>{proj.accuracy_band}</div>
              <div style={muted}>Stated forecast accuracy band</div>
            </div>
          </div>

          <Card title="Forecast vs actual">
            <div style={legendRow}>
              <span style={legendItem}>
                <span style={{ ...legendDot, background: colors.action }} /> Actual
              </span>
              <span style={legendItem}>
                <span style={{ ...legendDot, background: colors.brandAccent }} /> Forecast
              </span>
            </div>
            <div style={chartWrap}>
              {proj.points.map((p) => (
                <div key={p.label} style={chartCol}>
                  <div style={chartBarTrack}>
                    {p.actual != null && (
                      <div
                        style={{ ...chartBar, height: `${(p.actual / projMax) * 100}%`, background: colors.action }}
                        title={`Actual ${fmtCurrency(p.actual)}`}
                      />
                    )}
                    <div
                      style={{
                        ...chartBar,
                        height: `${(p.forecast / projMax) * 100}%`,
                        background: p.actual != null ? colors.brandAccent : `repeating-linear-gradient(45deg, ${colors.brandAccent}, ${colors.brandAccent} 4px, ${colors.brandTint} 4px, ${colors.brandTint} 8px)`,
                        opacity: p.actual != null ? 0.85 : 1,
                      }}
                      title={`Forecast ${fmtCurrency(p.forecast)}`}
                    />
                  </div>
                  <div style={chartColLabel}>{p.label}</div>
                </div>
              ))}
            </div>
          </Card>

          <AIInsightCard
            heading="Revenue projection — advisory forecast"
            acceptLabel="Accept forecast"
            readOnly
            style={{ marginTop: space.lg }}
          >
            The model projects {fmtCurrency(proj.projected_total)} in portfolio revenue capture (stated
            accuracy {proj.accuracy_band}), tracking actuals through the latest closed month. Forecast
            months (hatched bars) are model estimates for planning only — confirm against the finance
            close before relying on them.
          </AIInsightCard>
        </>
      )}
    </>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} style={{ ...tabBtn, ...(active ? tabBtnActive : null) }}>
      {children}
    </button>
  );
}

// --- styles ---------------------------------------------------------------

const muted: CSSProperties = { color: colors.textSecondary, fontSize: '0.86rem' };
const seedNotice: CSSProperties = {
  margin: '0 0 16px',
  padding: '8px 12px',
  background: colors.brandTint,
  color: colors.brandActive,
  borderRadius: radius.sm,
  fontSize: '0.82rem',
};
const tabs: CSSProperties = { display: 'flex', gap: 4 };
const tabBtn: CSSProperties = {
  padding: '7px 14px',
  background: 'transparent',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.pill,
  fontSize: '0.85rem',
  fontWeight: 600,
  color: colors.textSecondary,
  cursor: 'pointer',
};
const tabBtnActive: CSSProperties = { background: colors.brandTint, color: colors.brandActive, borderColor: colors.brandTint };

const projHeadRow: CSSProperties = { display: 'flex', gap: 12, marginBottom: space.lg };
const projHeadCard: CSSProperties = {
  background: colors.white,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: '16px 18px',
};
const projHeadValue: CSSProperties = { fontSize: '1.7rem', fontWeight: 800, color: colors.textPrimary };

const legendRow: CSSProperties = { display: 'flex', gap: 16, marginBottom: 10 };
const legendItem: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: colors.textSecondary };
const legendDot: CSSProperties = { width: 10, height: 10, borderRadius: 2 };

const chartWrap: CSSProperties = { display: 'flex', alignItems: 'flex-end', gap: 10, height: 200, paddingTop: 8 };
const chartCol: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' };
const chartBarTrack: CSSProperties = {
  flex: 1,
  width: '100%',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  gap: 3,
  minHeight: 0,
};
const chartBar: CSSProperties = { width: 16, borderRadius: `${radius.sm} ${radius.sm} 0 0` };
const chartColLabel: CSSProperties = { fontSize: '0.74rem', color: colors.textSecondary, marginTop: 6 };
